import * as React from "react";

/**
 * Section id → screen. Each domain folder owns its own map so screens can
 * be added without touching a shared route file. A section missing here
 * renders the placeholder.
 */
export type SectionComponent = React.LazyExoticComponent<React.ComponentType>;
export type SectionMap = Record<string, SectionComponent>;

const lazy = (loader: () => Promise<{ default: React.ComponentType }>) => React.lazy(loader);

export const pbx: SectionMap = {
  overview: lazy(() => import("./pbx/overview-page")),
  users: lazy(() => import("./pbx/users-page")),
  devices: lazy(() => import("./pbx/devices-page")),
  groups: lazy(() => import("./pbx/groups-page")),
  "call-center": lazy(() => import("./pbx/call-center-page")),
  voicemail: lazy(() => import("./pbx/voicemail-page")),
  conferences: lazy(() => import("./pbx/conferences-page")),
  fax: lazy(() => import("./pbx/fax-page")),
  media: lazy(() => import("./pbx/media-page")),
  directories: lazy(() => import("./pbx/directories-page")),
  "feature-codes": lazy(() => import("./pbx/feature-codes-page")),
  settings: lazy(() => import("./pbx/settings-page")),
  provisioning: lazy(() => import("./pbx/provisioning-page")),
};

export const numbers: SectionMap = {
  "in-service": lazy(() => import("./numbers/in-service-page")),
  spare: lazy(() => import("./numbers/spare-page")),
  buy: lazy(() => import("./numbers/buy-page")),
  ports: lazy(() => import("./numbers/ports-page")),
};

export const routing: SectionMap = {
  callflows: lazy(() => import("./routing/callflows-page")),
  menus: lazy(() => import("./routing/menus-page")),
  "time-of-day": lazy(() => import("./routing/time-of-day-page")),
  blacklists: lazy(() => import("./routing/blacklists-page")),
  dialplans: lazy(() => import("./routing/dialplans-page")),
};

export const insight: SectionMap = {
  live: lazy(() => import("./insight/live-page")),
  queues: lazy(() => import("./insight/queues-page")),
  history: lazy(() => import("./insight/history-page")),
  registrations: lazy(() => import("./insight/registrations-page")),
  recordings: lazy(() => import("./insight/recordings-page")),
  alerts: lazy(() => import("./insight/alerts-page")),
  webhooks: lazy(() => import("./insight/webhooks-page")),
  "api-access": lazy(() => import("./insight/api-access-page")),
};

export const accounts: SectionMap = {
  browse: lazy(() => import("./accounts/browse-page")),
  new: lazy(() => import("./accounts/new-page")),
};

export const platform: SectionMap = {
  carriers: lazy(() => import("./platform/carriers-page")),
  rates: lazy(() => import("./platform/rates-page")),
  ports: lazy(() => import("./platform/ports-page")),
  numbers: lazy(() => import("./platform/numbers-page")),
  notifications: lazy(() => import("./platform/notifications-page")),
  "system-configs": lazy(() => import("./platform/system-configs-page")),
  stir: lazy(() => import("./platform/stir-page")),
  provisioning: lazy(() => import("./platform/provisioning-page")),
  tasks: lazy(() => import("./platform/tasks-page")),
  status: lazy(() => import("./platform/status-page")),
  storage: lazy(() => import("./platform/storage-page")),
  whitelabel: lazy(() => import("./platform/whitelabel-page")),
};

export const registry: Record<string, SectionMap> = { pbx, numbers, routing, insight, accounts, platform };

/**
 * Domains that replace the section list in the context column with their
 * own component (Routing lists callflows; Accounts will list the tree).
 * The component takes the same props as `ContextColumn`.
 */
export interface ColumnProps {
  accountId?: string;
  section?: string;
  /** The `:id` route segment, when the section shows one object. */
  id?: string;
  hidden?: Set<string>;
  onNavigate?: () => void;
}
export interface ColumnEntry {
  Component: React.LazyExoticComponent<React.ComponentType<ColumnProps>>;
  /** Tailwind width class for the desktop column. */
  width: string;
}
export const columns: Record<string, ColumnEntry> = {
  routing: { Component: lazy(() => import("./routing/column")), width: "w-64" },
  accounts: { Component: lazy(() => import("./accounts/column")), width: "w-64" },
};
